import { CalendarOutlined, CloseOutlined, ExportOutlined, RightOutlined } from '@ant-design/icons';
import { t } from 'i18next';
import Draggable from 'react-draggable';
import { TbWorldLatitude, TbWorldLongitude } from 'react-icons/tb';
import DataViewer from 'src/app/components/DataViewer/DataViewer';
import { MapMarkerController } from 'src/app/components/LeafletMapComponent/controllers/MapMarkerController';
import styles from 'src/app/components/LeafletMapComponent/InterestMarkerInfoBox/InterestMarkerInfoBox.module.scss';
import { CustomMarker } from 'src/app/components/LeafletMapComponent/leaflet.config';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import { getDateString } from 'src/app/helpers/time.helper';
import { useAppSelector } from 'src/app/redux/hook';
import { getBlock } from 'src/app/services/surfer-service/surfer.service';

interface Props {
  selectedInterest: CustomMarker;
  mapMarkerController: MapMarkerController | undefined;
}

const InterestMarkerInfoBox = ({ selectedInterest, mapMarkerController }: Props) => {
  const isPrev = selectedInterest.id.split('-')[0] === 'sub';
  const { enterpriseIdentifyClass } = useAppSelector((state) => state.user);

  const onRowClick = async (blockId: string) => {
    const { data: blockData } = await getBlock({
      blockID: blockId,
      enterpriseId: enterpriseIdentifyClass.id || null,
    });
    showModal({
      title: t('nSearchPage.data'),
      content: <DataViewer blockData={blockData} />,
      hasFooter: false,
      size: 'large',
      backgroundColor: 'var(--header_bg)',
    });
  };

  const { theme } = useAppSelector((state) => state.configContent);
  return (
    <Draggable defaultPosition={{ x: 25, y: 500 }} bounds="body">
      <div className={styles.infoBox}>
        <div
          style={{
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingInline: '1rem',
            paddingBlock: '.5rem',
            background: theme.colors.quoteBgc,
          }}
        >
          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
            <img
              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
              alt="marker"
              // @ts-ignore
              // eslint-disable-next-line no-underscore-dangle
              src={selectedInterest.marker?._icon?.src}
            />
            <span>{selectedInterest.label}</span>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <span
              style={{
                display: 'flex',
                gap: '5px',
                background: isPrev ? '#DBEAFE' : '#DDFBE9',
                borderRadius: '20px',
                padding: '2px 5px',
                alignItems: 'center',
                color: isPrev ? '#2563EB' : '#2FA66C',
              }}
            >
              <div
                style={{ width: '5px', height: '5px', background: isPrev ? '#2563EB' : '#2FA66C', borderRadius: '50%' }}
              />
              <span style={{ fontSize: '10px' }}>{isPrev ? 'Previous' : 'Current'}</span>
            </span>
            <CloseOutlined
              onClick={() => {
                if (mapMarkerController) {
                  if (mapMarkerController.setSelectedInterestAction) {
                    mapMarkerController.setSelectedInterestAction(undefined);
                  }
                }
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            paddingInline: '1rem',
            fontSize: '12px',
            color: 'var(--primary_text_color)',
            background: 'var(--sidebar_bg)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingBlock: '8px',
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CalendarOutlined />
              <span style={{ color: 'var(--secondary_text_color)' }}>Date & Time</span>
            </div>
            <span>{getDateString(selectedInterest.time)}</span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingBlock: '8px',
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TbWorldLatitude />
              <span style={{ color: 'var(--secondary_text_color)' }}>Latitude</span>
            </div>
            <span>{selectedInterest.coordinates[0]?.toFixed(6)}</span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingBlock: '8px',
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TbWorldLongitude />
              <span style={{ color: 'var(--secondary_text_color)' }}>Longitude</span>
            </div>
            <span>{selectedInterest.coordinates[1]?.toFixed(6)}</span>
          </div>
          <div
            style={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBlock: '10px',
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }}
            onClick={() => onRowClick(selectedInterest.blockID)}
          >
            <div style={{ display: 'flex', gap: '10px' }}>
              <ExportOutlined style={{ fontSize: '20px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>Open Details</span>
                <span style={{ fontSize: '9px' }}>View full information in a modal</span>
              </div>
            </div>
            <RightOutlined />
          </div>
        </div>
        {/* <button
          type="button"
          onClick={() => {
            if (mapMarkerController) {
              if (mapMarkerController.setSelectedInterestAction) {
                mapMarkerController.setSelectedInterestAction(undefined);
              }
            }
          }}
          className={styles.overlappedCloseBtn}
        >
          x
        </button> */}
        {/* <h1>{selectedInterest.label}</h1>
        <div className={styles.infoBoxInfos}>
          {selectedInterest.time && <p>When: {getDateString(selectedInterest.time)}</p>}
          <p>Latitude: {selectedInterest.coordinates[0].toFixed(6)}</p>
          <p>Longitude: {selectedInterest.coordinates[1].toFixed(6)}</p>
        </div> */}
      </div>
    </Draggable>
  );
};

export default InterestMarkerInfoBox;
